import { SearchGroup } from '@converge/graphql';
import { ValueOf } from '@converge/utils';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconProps, SvgIconTypeMap } from '@mui/material/SvgIcon/SvgIcon';
import { lazy, useMemo } from 'react';

export enum ConvergeIconType {
  Submissions = 'submissions',
  Insureds = 'insureds',
  Aliases = 'aliases',
  Brokers = 'brokers',
  Endorsements = 'endorsements',
  Policies = 'policies',
  Quotes = 'quotes',
  Subjectivities = 'subjectivities',
  Entity = 'entity',
  Warning = 'warning',
  Claim = 'claim',
}

export type ConvergeIconValue = ValueOf<ConvergeIconType>;

export enum ConvergeIconVariant {
  Outlined = 'outlined',
  Solid = 'solid',
  Rounded = 'rounded',
  TwoTone = 'two-tone',
  Sharp = 'sharp',
}

export type ConvergeIconVariantValue = ValueOf<ConvergeIconVariant>;

export interface IConvergeIconProps extends Omit<SvgIconProps, 'type'> {
  type: ConvergeIconValue;
  variant?: ConvergeIconVariantValue;
}

const LAZY_ICON_LOADER: Record<
  ConvergeIconType,
  Record<
    ConvergeIconVariant,
    React.LazyExoticComponent<OverridableComponent<SvgIconTypeMap>>
  >
> = {
  [ConvergeIconType.Submissions]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/AllInboxOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/AllInbox')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/AllInboxTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/AllInboxRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/AllInboxSharp')
    ),
  },
  [ConvergeIconType.Warning]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/WarningOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/Warning')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/WarningTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/WarningRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/WarningSharp')
    ),
  },
  [ConvergeIconType.Entity]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/WorkOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(() => import('@mui/icons-material/Work')),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/WorkTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/WorkRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/WorkSharp')
    ),
  },
  [ConvergeIconType.Claim]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/WorkOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(() => import('@mui/icons-material/Work')),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/WorkTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/WorkRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/WorkSharp')
    ),
  },
  [ConvergeIconType.Insureds]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/BusinessOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/Business')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/BusinessTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/BusinessRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/BusinessSharp')
    ),
  },
  [ConvergeIconType.Aliases]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/BadgeOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/Badge')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/BadgeTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/BadgeRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/BadgeSharp')
    ),
  },
  [ConvergeIconType.Brokers]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/BusinessCenterOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/BusinessCenter')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/BusinessCenterTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/BusinessCenterRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/BusinessCenterSharp')
    ),
  },
  [ConvergeIconType.Endorsements]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/PostAddOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/PostAdd')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/PostAddTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/PostAddRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/PostAddSharp')
    ),
  },
  [ConvergeIconType.Policies]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/ShieldOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/Shield')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/ShieldTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/ShieldRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/ShieldSharp')
    ),
  },
  [ConvergeIconType.Quotes]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/RequestQuoteOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/RequestQuote')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/RequestQuoteTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/RequestQuoteRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/RequestQuoteSharp')
    ),
  },
  [ConvergeIconType.Subjectivities]: {
    [ConvergeIconVariant.Outlined]: lazy(
      () => import('@mui/icons-material/ApprovalOutlined')
    ),
    [ConvergeIconVariant.Solid]: lazy(
      () => import('@mui/icons-material/Approval')
    ),
    [ConvergeIconVariant.TwoTone]: lazy(
      () => import('@mui/icons-material/ApprovalTwoTone')
    ),
    [ConvergeIconVariant.Rounded]: lazy(
      () => import('@mui/icons-material/ApprovalRounded')
    ),
    [ConvergeIconVariant.Sharp]: lazy(
      () => import('@mui/icons-material/ApprovalSharp')
    ),
  },
};

export const ConvergeIcon = ({
  type,
  variant = ConvergeIconVariant.Solid,
  ...props
}: IConvergeIconProps) => {
  const Icon = useMemo(() => LAZY_ICON_LOADER[type][variant], [type, variant]);

  return <Icon {...props} />;
};

interface IConvergeSearchGroupIconProps
  extends Omit<IConvergeIconProps, 'type'> {
  type: SearchGroup | 'OTHER';
}

export const ConvergeSearchGroupIcon = ({
  type,
  ...props
}: IConvergeSearchGroupIconProps): JSX.Element | null => {
  let convergeIconType: ConvergeIconType;
  switch (type) {
    case SearchGroup.Entity:
      convergeIconType = ConvergeIconType.Entity;
      break;
    case SearchGroup.Insured:
      convergeIconType = ConvergeIconType.Insureds;
      break;
    case SearchGroup.Alias:
      convergeIconType = ConvergeIconType.Aliases;
      break;
    case SearchGroup.Broker:
      convergeIconType = ConvergeIconType.Brokers;
      break;
    case SearchGroup.Endorsement:
      convergeIconType = ConvergeIconType.Endorsements;
      break;
    case SearchGroup.Policy:
      convergeIconType = ConvergeIconType.Policies;
      break;
    case SearchGroup.Quote:
      convergeIconType = ConvergeIconType.Quotes;
      break;
    case SearchGroup.Subjectivity:
      convergeIconType = ConvergeIconType.Subjectivities;
      break;
    case SearchGroup.Submission:
      convergeIconType = ConvergeIconType.Submissions;
      break;
    default:
      return null;
  }
  return <ConvergeIcon type={convergeIconType} {...props} />;
};
